import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type {
  SuperadminAdmin,
  SuperadminAdminRequest,
} from "@/lib/api-types";

/**
 * Superadmin dashboard hooks (superadmin scope).
 *
 * All endpoints below live under /api/superadmin/... and require a JWT with
 * role: 'SUPERADMIN' (see [[project-shared-cookie]] — same cookie as workspace
 * users, differentiated by payload). The route guard is
 * `<RequireSuperadmin />` in `src/components/auth/require-superadmin.tsx`.
 *
 * Login/logout/session hooks live in `use-auth.tsx` for symmetry with the
 * workspace login hooks. This file covers the data + mutations the dashboard
 * needs after the user is already authenticated.
 */

const ADMINS_KEY = ["superadmin", "admins"] as const;
const REQUESTS_KEY = ["superadmin", "requests"] as const;

// ── Queries ──────────────────────────────────────────────────────────────────

/**
 * GET /api/superadmin/admins — every ADMIN row with live storage aggregates.
 * Server computes `currentStorageBytes` per admin in a SUM join, `peakStorageBytes`
 * comes straight off the users row. Small list (dozens, not thousands) so no
 * pagination; refetches on window focus so extend/delete elsewhere reflect.
 */
export function useSuperadminAdmins() {
  return useQuery<SuperadminAdmin[]>({
    queryKey: ADMINS_KEY,
    queryFn: () => api.get<SuperadminAdmin[]>("/superadmin/admins"),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
}

/**
 * GET /api/superadmin/requests — PENDING admin sign-up requests only.
 * Backend filters status='PENDING' server-side and orders by createdAt asc
 * so the oldest request is at the top (FIFO review queue).
 */
export function useSuperadminRequests() {
  return useQuery<SuperadminAdminRequest[]>({
    queryKey: REQUESTS_KEY,
    queryFn: () => api.get<SuperadminAdminRequest[]>("/superadmin/requests"),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
}

// ── Mutations ────────────────────────────────────────────────────────────────

/**
 * POST /api/superadmin/requests/:id/approve — creates an ADMIN user with a
 * temp password, emails the applicant, marks the request APPROVED. Invalidates
 * both the requests queue (row disappears) and the admins list (new row appears).
 */
export function useSuperadminApproveRequest() {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, Error, { id: string }>({
    mutationFn: ({ id }) =>
      api.post<{ ok: true }>(`/superadmin/requests/${id}/approve`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: REQUESTS_KEY });
      qc.invalidateQueries({ queryKey: ADMINS_KEY });
    },
  });
}

/**
 * POST /api/superadmin/requests/:id/reject — sends the rejection email, marks
 * the request REJECTED. No admin created, no admins-list invalidation needed.
 */
export function useSuperadminRejectRequest() {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, Error, { id: string }>({
    mutationFn: ({ id }) =>
      api.post<{ ok: true }>(`/superadmin/requests/${id}/reject`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: REQUESTS_KEY });
    },
  });
}

/**
 * POST /api/superadmin/admins/:id/expire — soft-suspend an admin.
 * Sets `expiresAt` to now; login gate + share-token routes reject the row.
 * Data is preserved (nothing deleted). Reversible via `useSuperadminRestoreAdmin`.
 *
 * Policy change 2026-07-23: admin accounts no longer auto-expire on a 40-day
 * timer. Expiry is now a manual superadmin action only.
 */
export function useSuperadminExpireAdmin() {
  const qc = useQueryClient();
  return useMutation<{ ok: true; expiresAt: string }, Error, { id: string }>({
    mutationFn: ({ id }) =>
      api.post<{ ok: true; expiresAt: string }>(`/superadmin/admins/${id}/expire`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ADMINS_KEY });
    },
  });
}

/**
 * POST /api/superadmin/admins/:id/restore — undo an expire. Clears
 * `expiresAt` back to NULL. Idempotent — no-op on an already-active admin.
 */
export function useSuperadminRestoreAdmin() {
  const qc = useQueryClient();
  return useMutation<{ ok: true; expiresAt: null }, Error, { id: string }>({
    mutationFn: ({ id }) =>
      api.post<{ ok: true; expiresAt: null }>(`/superadmin/admins/${id}/restore`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ADMINS_KEY });
    },
  });
}

/**
 * POST /api/superadmin/admins/:id/grant-trial — flip a Free account to a 15-day
 * TRIAL (150 GB, full features) and fire the welcome email. Free/Trial only.
 */
export function useSuperadminGrantTrial() {
  const qc = useQueryClient();
  return useMutation<{ ok: true; plan: string; trialEndsAt: string }, Error, { id: string }>({
    mutationFn: ({ id }) =>
      api.post<{ ok: true; plan: string; trialEndsAt: string }>(`/superadmin/admins/${id}/grant-trial`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ADMINS_KEY });
    },
  });
}

/**
 * DELETE /api/superadmin/admins/:id — full cascade: R2 objects, videos,
 * projects, editor invites, storage snapshots, upload sessions, all workspace
 * members (FULL_ACCESS / EDIT_SHARE / EDIT), then the admin user themselves.
 *
 * Irreversible. Frontend should confirm before calling.
 */
export function useSuperadminDeleteAdmin() {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, Error, { id: string }>({
    mutationFn: ({ id }) =>
      api.delete<{ ok: true }>(`/superadmin/admins/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ADMINS_KEY });
    },
  });
}
